import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Loader2, Archive, Eye, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { trainingsAPI } from '../../lib/api';
import { TRAINING_CATEGORY_LABELS } from './classificationGroups';

const ACTIONS = {
  arquivar: { label: 'Arquivar', icon: Archive, cls: 'bg-red-100 text-red-700' },
  revisar: { label: 'Revisão manual', icon: Eye, cls: 'bg-amber-100 text-amber-700' },
  recategorizar: { label: 'Recategorizar', icon: Tag, cls: 'bg-cyan-100 text-cyan-700' },
};
const catLabel = (c) => TRAINING_CATEGORY_LABELS[c] || c || '-';

export const TrainingReclassifyDialog = ({ open, onClose, onApplied }) => {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    trainingsAPI.reclassificationPreview()
      .then((r) => { setPreview(r.data); setSelected(r.data.items.map((i) => i.training_id)); })
      .catch(() => { toast.error('Erro ao gerar simulação'); onClose(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const apply = async () => {
    setApplying(true);
    try {
      const r = await trainingsAPI.reclassificationApply(selected);
      const a = r.data.aplicados;
      toast.success(`Aplicado: ${a.arquivar} arquivado(s), ${a.revisar} para revisão, ${a.recategorizar} recategorizado(s)`);
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aplicar ajustes');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="reclassify-dialog">
        <DialogHeader>
          <DialogTitle>Revisar Classificação do Catálogo</DialogTitle>
          <DialogDescription>
            Simulação: nada é alterado até você confirmar. Itens que são Requisitos do Cargo são arquivados (nunca excluídos);
            se tiverem histórico, ficam marcados para revisão manual.
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
        ) : preview.items.length === 0 ? (
          <p className="py-8 text-center text-slate-500" data-testid="reclassify-empty">Catálogo já está classificado corretamente.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2 text-xs" data-testid="reclassify-summary">
              {Object.entries(ACTIONS).map(([k, a]) => (
                <span key={k} className={`px-2 py-1 rounded-full ${a.cls}`}>{a.label}: {preview.resumo[k]}</span>
              ))}
            </div>
            <div className="border rounded-lg divide-y">
              {preview.items.map((it) => {
                const a = ACTIONS[it.acao];
                const Icon = a.icon;
                return (
                  <label key={it.training_id} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50" data-testid={`reclassify-row-${it.training_id}`}>
                    <Checkbox checked={selected.includes(it.training_id)} onCheckedChange={() => toggle(it.training_id)} className="mt-0.5"
                      data-testid={`reclassify-check-${it.training_id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{it.nome}</p>
                      <p className="text-xs text-slate-500">{it.motivo}</p>
                      {it.acao === 'recategorizar' && (
                        <p className="text-xs text-slate-600 mt-0.5">{catLabel(it.categoria_atual)} → <strong>{catLabel(it.categoria_sugerida)}</strong></p>
                      )}
                      {it.registros_vinculados > 0 && (
                        <p className="text-xs text-amber-700 mt-0.5">{it.registros_vinculados} registro(s) vinculado(s)</p>
                      )}
                    </div>
                    <Badge className={`${a.cls} shrink-0`}><Icon className="w-3 h-3 mr-1" />{a.label}</Badge>
                  </label>
                );
              })}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reclassify-cancel-btn">Cancelar</Button>
          <Button onClick={apply} disabled={applying || !preview || selected.length === 0} className="bg-cyan-600 hover:bg-cyan-700" data-testid="reclassify-apply-btn">
            {applying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Aplicar {selected.length} ajuste(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
